import { Injectable, inject } from '@angular/core';

import {
    CUSTOMER_CATEGORY,
    CartModel,
    Product,
    ProductFactory,
    QuoteContextRenewalModel,
    QuoteModel,
    Sim,
} from '@bytel/bytel-sales';
import { CheckoutCartModel, UserService } from '@bytel/checkout';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PriceTypeUtils } from '../helper/price-type.helper';
import {
    ISimApi,
    ISimApiPayload,
    ISimApiResponse,
    ISimProductApiPayload,
    ISimQuoteApi,
    ISimQuoteApiPayload,
} from '../interfaces/sim-api.interface';
import { ISim, SIM_TYPES } from '../interfaces/sim.interace';
import { SimContextModel } from '../models/sim-context.model';
import { CheckoutOauth2ResourcesService } from '../services/oauth2-ressources.service';

@Injectable({
    providedIn: 'root',
})
export class SimRepository {
    protected userService: UserService = inject(UserService);
    protected oauth2Resource: CheckoutOauth2ResourcesService = inject(CheckoutOauth2ResourcesService);

    public GetSimContext(cartModel: CheckoutCartModel): Observable<SimContextModel[]> {
        // const payloadSchema = z.object({
        //     idPU: z.string().optional(),
        //     clientCategorie: z.string(),
        //     parcours: z.array(
        //         z.object({
        //             id: z.string(),
        //             idContrat: z.string().optional(),
        //             produits: z.array(
        //                 z.object({
        //                     id: z.string(),
        //                     gencode: z.string(),
        //                 }),
        //             ),
        //             estCourant: z.boolean(),
        //             type: z.string(),
        //         }),
        //     ),
        // });

        const payload = this._getSimContextPayload(cartModel);
        return this.oauth2Resource
            .useSalesApi()
            .addCache()
            .simsContextualises()
            .post(payload)
            .pipe(
                map((response: ISimApiResponse) => response.parcours.map((quote) => this._simResponseToModel(quote))),
            );
    }

    private _getSimContextPayload(cart: CartModel): ISimApiPayload {
        const payload: ISimApiPayload = {
            clientCategorie: cart.isPro ? CUSTOMER_CATEGORY.PRO : CUSTOMER_CATEGORY.GP,
            parcours: cart.quotes.map((quote) => this._getSimContextQuotePayload(quote)),
        };

        if (this.userService.user.id) {
            payload.idPU = this.userService.user.id;
        }

        return payload;
    }

    private _getSimContextQuotePayload(quote: QuoteModel): ISimQuoteApiPayload {
        const quotePayload: ISimQuoteApiPayload = {
            produits: quote.products
                .filter((product) => !ProductFactory.Is(product, Sim))
                .map((product) => this._getSimContextProductPayload(product)),
            estCourant: true,
            type: quote.context.id,
        };

        if (quote.id) {
            quotePayload.id = quote.id.toString();
        }

        if (quote.context instanceof QuoteContextRenewalModel) {
            quotePayload.idContrat = quote.context.contractId.toString();
        }
        return quotePayload;
    }

    private _getSimContextProductPayload(product: Product): ISimProductApiPayload {
        return {
            id: product?.id?.toString(),
            gencode: product.gencode,
        };
    }

    private _simResponseToModel(response: ISimQuoteApi): SimContextModel {
        return new SimContextModel({
            id: response.id,
            isCurrent: response.estCourant,
            isCompatible: response.simCompatible,
            sims: response.simsCompatibles
                .filter((sim) => sim.type !== SIM_TYPES.ESIM)
                .map((sim) => this._simApiToSim(sim)),
        });
    }

    private _simApiToSim(simApi: ISimApi): ISim {
        return {
            gencode: simApi.gencode,
            isCompatibleTerminal: simApi.terminalCompatible,
            isPrefered: simApi.prefere,
            price: simApi.prix,
            priceType: PriceTypeUtils.priceTypeSimToEnum(simApi.typePrix),
            type: simApi.type as SIM_TYPES,
        };
    }
}
